import React from 'react';

import { Box, Divider, Grid, Stack } from '@mui/material';
import { GenericInfoLabel } from 'src/v2/components/ui/label';
import { LabelApr } from 'src/v2/components/ui/label/stats';
import { LoadSkeleton } from 'src/v2/components/ui/loadSkeleton';
import { ProgressBar } from 'src/v2/components/ui/progressBar';

import { useSession } from '~components/hooks/useSession';
import Link from '~components/shared/link';
import { black } from '~components/theme/styles/colors';
import { columnGap, secondaryBorder } from '~components/theme/styles/mixins';
import { EDisplayType } from '~components/types';
import { displayFormatUnits } from '~components/utils/utils';
import { EVestaAsset } from '~config/types';

import { ButtonWithIcon } from '../../../../ui/buttons/buttonWithIcon';
import { AssetTitle } from '../../../../ui/icons/assetTitle';

import { useDefaultVaultCard } from './useDefaultVaultCard';

export const DefaultVaultCard = ({ asset }: { asset: EVestaAsset }) => {
  const {
    mcr,
    apr,
    vrr,
    mintCap,
    vaultOpen,
    currentAssetDebt,
    isVestaParamsLoading,
    isVaultUserLoading,
    isAprLoading,
    isAnyVaultLoading,
    isInterestLoading,
  } = useDefaultVaultCard(asset);
  const { isConnected } = useSession();

  return (
    <Box
      sx={{
        flexShrink: 0,
        padding: '32px',
        width: '100%',
        maxWidth: ['inital', '341.33px'],
        borderRadius: '8px',
        background: black['45%'],
        border: `1px solid ${black['15%']}`,
        backgroundClip: 'padding-box',
        position: 'relative',
        '&:hover': {
          ...secondaryBorder,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        <AssetTitle secondary asset={asset} active={vaultOpen} sx={{ mb: '24px' }} />
        <Grid sx={{ ...columnGap(24) }}>
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
            <Box>
              <GenericInfoLabel
                variant="vertical"
                labelTitle="MCR"
                labelValueVariant={'label2'}
                labelValue={displayFormatUnits({ value: mcr, displayType: EDisplayType.Percent, decimal: 0 })}
                isLoading={isVestaParamsLoading}
                skeletonProps={{
                  height: '24px',
                }}
              />
            </Box>
            {!!apr && <LabelApr variant="header" apr={apr} />}
          </Stack>
          <Grid sx={{ ...columnGap(8) }}>
            <ProgressBar
              asset={asset}
              value={(currentAssetDebt / mintCap) * 100 || 0}
              isLoading={isAnyVaultLoading || isVestaParamsLoading}
            />
            <GenericInfoLabel
              labelTitle={'MINTED'}
              labelValueVariant={'body3'}
              labelValue={`${displayFormatUnits({
                value: currentAssetDebt,
                displayType: EDisplayType.ShortVST,
                decimal: 0,
              })} of ${displayFormatUnits({
                value: mintCap,
                displayType: EDisplayType.ShortVST,
                decimal: 0,
              })}`}
              isLoading={isAnyVaultLoading || isVestaParamsLoading}
            />
          </Grid>
          <Divider sx={{ margin: 0 }} />
          <Grid sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <GenericInfoLabel
              labelValue={displayFormatUnits({ value: vrr, displayType: EDisplayType.Percent })}
              labelTitle={'VESTA REFERENCE RATE'}
              isLoading={isInterestLoading}
            />
            <GenericInfoLabel
              labelValue={displayFormatUnits({
                value: apr || NaN,
                displayType: EDisplayType.Percent,
              })}
              labelTitle={'YIELD ON COLLATERAL'}
              isLoading={isAprLoading}
            />
          </Grid>
        </Grid>
        <LoadSkeleton isLoading={isVaultUserLoading && isConnected} height={42} width="100%">
          <Link href={`/vaults/${asset}`}>
            <ButtonWithIcon variant={vaultOpen ? 'primaryGradient' : undefined}>
              {vaultOpen ? 'EXISTING VAULT' : 'CREATE VAULT'}
            </ButtonWithIcon>
          </Link>
        </LoadSkeleton>
      </Box>
    </Box>
  );
};
